// frontend/src/widgets/WorkflowDraftHeaderWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-04-15
// Updated:      2026-10-06 15:35 BST
//
// Change Log (GMT/BST):
//   2026-10-06 15:35 BST - Corrected workflow draft provenance from unsupported "manual"
//                           to canonical "ui" source_type.
//
// Description:
//   Workflow draft / version context widget.
//
//   Responsibilities:
//     - Show selected template context
//     - Show selected version context
//     - Update template metadata
//     - Create draft clone from current template / version
//     - Publish selected draft version
//     - Use governed workflow template categories
//
//   Important:
//     - Controlled by WorkflowPage
//     - Draft editing is template-metadata only in this first pass
//     - Published versions remain read-only from a version perspective
//     - category_id is now the governed write path
//     - category/category_name are retained only for compatibility display
//
// Mobile Responsiveness (2026-09-10):
//     - Preserves template metadata editing, draft creation, publishing and governed category logic.
//     - Desktop retains the existing template editor + Selected Version side-panel layout.
//     - Mobile stacks the template editor above Selected Version using the same render tree.
//     - Template Key / Category collapse to one column on phones; Name and Description remain full-width.
//     - Status/category context wraps safely instead of overflowing narrow widgets.
//     - Save Template / Create Draft / Publish Draft become full-width touch targets on mobile.
//     - Form controls and System template checkbox become touch-safe on mobile.
//     - Dynamic mobile sizing uses the proven Vision full-form pattern:
//         restore previous host sizing;
//         clear temporary root/content sizing;
//         measure the complete natural rendered content;
//         wait a second animation frame;
//         resize the first width-matching fixed-height mobile host.
//     - ResizeObserver watches the full content so template/version context changes cannot leave the widget clipped.
//     - The no-template state also uses natural mobile height.
//     - No duplicate mobile render tree is mounted.
//
// Operational Notes:
//     - onSaveTemplate, onCreateDraft and onPublishVersion remain the sole mutation paths.
//     - Published-version read-only behaviour, draft-only publish gating and category_id writes are unchanged.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";

const WorkflowDraftHeaderWidget = ({
  selectedTemplate = null,
  selectedVersion = null,
  workflowTemplateCategories = [],
  loading = {
    savingTemplate: false,
    creatingDraft: false,
    publishing: false,
  },
  onSaveTemplate,
  onCreateDraft,
  onPublishVersion,
}) => {
  const { theme } = useTheme();

  const widgetRootRef = useRef(null);
  const contentRef = useRef(null);

  const [form, setForm] = useState({
    template_key: "",
    name: "",
    description: "",
    category_id: "",
    status: "",
    is_system_template: false,
  });

  useEffect(() => {
    setForm({
      template_key: selectedTemplate?.template_key || "",
      name: selectedTemplate?.name || "",
      description: selectedTemplate?.description || "",
      category_id:
        selectedTemplate?.category_id !== null && selectedTemplate?.category_id !== undefined
          ? String(selectedTemplate.category_id)
          : "",
      status: selectedTemplate?.status || "",
      is_system_template: !!selectedTemplate?.is_system_template,
    });
  }, [selectedTemplate]);

  const readOnlyVersion = selectedVersion?.status === "published";
  const publishable = selectedVersion?.status === "draft";

  const resolvedCategoryName = useMemo(() => {
    if (selectedTemplate?.category_name) return selectedTemplate.category_name;
    if (selectedTemplate?.category) return selectedTemplate.category;
    if (
      selectedTemplate?.category_id !== null &&
      selectedTemplate?.category_id !== undefined &&
      Array.isArray(workflowTemplateCategories)
    ) {
      const match = workflowTemplateCategories.find(
        (item) => String(item.id) === String(selectedTemplate.category_id)
      );
      return match?.name || null;
    }
    return null;
  }, [selectedTemplate, workflowTemplateCategories]);

  const activeCategoryOptions = useMemo(() => {
    return Array.isArray(workflowTemplateCategories)
      ? workflowTemplateCategories
          .filter((item) => item && item.is_active !== false)
          .sort((a, b) => {
            const sortA = Number(a?.sort_order || 0);
            const sortB = Number(b?.sort_order || 0);
            if (sortA !== sortB) return sortA - sortB;
            return String(a?.name || "").localeCompare(String(b?.name || ""));
          })
      : [];
  }, [workflowTemplateCategories]);

  const actionBtn = {
    background: theme.button_action?.normal || "#000",
    color: "#fff",
    padding: "8px 12px",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
  };

  const generalBtn = {
    background: theme.button_general?.normal || "#222",
    color: "#fff",
    padding: "8px 12px",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
  };

  const inputStyle = {
    width: "100%",
    minHeight: 44,
    padding: "8px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    boxSizing: "border-box",
    fontSize: 14,
    backgroundColor: "#fff",
  };

  const labelStyle = {
    fontSize: 13,
    fontWeight: 700,
    color: "#374151",
    marginBottom: 4,
  };

  const muted = {
    fontSize: 12,
    color: "#6b7280",
  };

  const statusBadgeStyle = (status) => ({
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    background:
      status === "published"
        ? "#dcfce7"
        : status === "draft"
          ? "#dbeafe"
          : status === "retired"
            ? "#f3f4f6"
            : "#fef3c7",
    color:
      status === "published"
        ? "#166534"
        : status === "draft"
          ? "#1d4ed8"
          : status === "retired"
            ? "#4b5563"
            : "#92400e",
  });

  const handleSaveTemplate = () => {
    if (!selectedTemplate?.id) {
      alert("No template selected.");
      return;
    }

    const payload = {
      template_key: form.template_key.trim(),
      name: form.name.trim(),
      description: form.description.trim() || null,
      category_id: form.category_id ? Number(form.category_id) : null,
      is_system_template: !!form.is_system_template,
    };

    if (!payload.template_key || !payload.name) {
      alert("Template key and name are required.");
      return;
    }

    onSaveTemplate?.(payload);
  };

  const handleCreateDraft = () => {
    if (!selectedTemplate?.id) {
      alert("No template selected.");
      return;
    }

    onCreateDraft?.({
      version_label: null,
      change_summary: null,
      source_type: "ui",
      source_reference: null,
      clone_from_version_id: selectedVersion?.id || null,
    });
  };

  const handlePublish = () => {
    if (!selectedTemplate?.id || !selectedVersion?.id) {
      alert("Select a draft version first.");
      return;
    }

    if (selectedVersion.status !== "draft") {
      alert("Only draft versions can be published.");
      return;
    }

    onPublishVersion?.({
      change_summary: selectedVersion.change_summary || null,
    });
  };

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const content = contentRef.current;

    if (!root || !content) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      root.style.height = "";
      root.style.minHeight = "";
      content.style.height = "";
      content.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const naturalContentHeight = Math.ceil(
          Math.max(
            content.getBoundingClientRect().height || 0,
            content.scrollHeight || 0
          )
        );

        root.style.height = "auto";
        root.style.minHeight =
          Number.isFinite(naturalContentHeight) && naturalContentHeight > 0
            ? `${naturalContentHeight}px`
            : "0px";

        content.style.height = "auto";
        content.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    resizeObserver?.observe(content);

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);

      restoreHost();

      root.style.height = "";
      root.style.minHeight = "";
      content.style.height = "";
      content.style.minHeight = "";
    };
  }, [
    activeCategoryOptions.length,
    loading.creatingDraft,
    loading.publishing,
    loading.savingTemplate,
    resolvedCategoryName,
    selectedTemplate?.id,
    selectedTemplate?.is_system_template,
    selectedTemplate?.status,
    selectedVersion?.id,
    selectedVersion?.status,
  ]);

  if (!selectedTemplate) {
    return (
      <div
        ref={widgetRootRef}
        className="h-auto min-h-[80px] w-full overflow-visible md:h-full"
      >
        <div
          ref={contentRef}
          className="flex min-h-[80px] items-center break-words text-[#6b7280]"
        >
          Select a workflow template to view draft and version context.
        </div>
      </div>
    );
  }

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
    >
      <style>{`
        @media (max-width: 767px) {
          .vx-workflow-draft-mobile-button {
            width: 100% !important;
            min-height: 44px;
            white-space: normal !important;
          }

          .vx-workflow-draft-content input,
          .vx-workflow-draft-content select,
          .vx-workflow-draft-content textarea {
            min-height: 44px;
          }
        }
      `}</style>

      <div
        ref={contentRef}
        className="vx-workflow-draft-content flex h-auto min-h-0 flex-col gap-3.5 md:h-full"
      >
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-between">
        <div>
          <div className="min-w-0 break-words text-lg font-bold">{selectedTemplate.name}</div>
          <div className="break-all" style={muted}>{selectedTemplate.template_key}</div>
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span style={statusBadgeStyle(selectedTemplate.status)}>{selectedTemplate.status}</span>
          {resolvedCategoryName ? <span style={muted}>Category: {resolvedCategoryName}</span> : null}
          {selectedTemplate.is_system_template ? <span style={muted}>System template</span> : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr] lg:items-start">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <div style={labelStyle}>Template Key</div>
            <input
              type="text"
              value={form.template_key}
              onChange={(e) => setForm((p) => ({ ...p, template_key: e.target.value }))}
              style={inputStyle}
            />
          </div>

          <div>
            <div style={labelStyle}>Category</div>
            <select
              value={form.category_id}
              onChange={(e) => setForm((p) => ({ ...p, category_id: e.target.value }))}
              style={inputStyle}
            >
              <option value="">No category</option>
              {activeCategoryOptions.map((category) => (
                <option key={category.id} value={String(category.id)}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <div style={labelStyle}>Template Name</div>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              style={inputStyle}
            />
          </div>

          <div className="md:col-span-2">
            <div style={labelStyle}>Description</div>
            <textarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              style={{ ...inputStyle, minHeight: 80, resize: "vertical" }}
            />
          </div>

          <div className="md:col-span-2">
            <label className="flex min-h-[44px] items-center gap-2 text-[13px]">
              <input
                className="h-5 w-5 md:h-auto md:w-auto"
                type="checkbox"
                checked={!!form.is_system_template}
                onChange={(e) => setForm((p) => ({ ...p, is_system_template: e.target.checked }))}
              />
              System template
            </label>
          </div>
        </div>

        <div
          className="min-w-0 break-words"
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            padding: 12,
            background: "#fafafa",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Selected Version</div>

          {selectedVersion ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div>
                <div style={{ fontWeight: 700 }}>
                  v{selectedVersion.version_no}
                  {selectedVersion.version_label ? ` — ${selectedVersion.version_label}` : ""}
                </div>
                <div style={muted}>Status: {selectedVersion.status}</div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span style={statusBadgeStyle(selectedVersion.status)}>{selectedVersion.status}</span>
                {selectedVersion.is_current ? <span style={muted}>Current</span> : null}
              </div>

              {selectedVersion.change_summary ? (
                <div style={muted}>{selectedVersion.change_summary}</div>
              ) : (
                <div style={muted}>No change summary</div>
              )}

              <div style={muted}>
                Created: {selectedVersion.created_at ? new Date(selectedVersion.created_at).toLocaleString() : "—"}
              </div>

              {readOnlyVersion && (
                <div style={{ fontSize: 12, color: "#92400e", fontWeight: 600 }}>
                  Published versions are read-only from a version lifecycle perspective.
                </div>
              )}
            </div>
          ) : (
            <div style={muted}>No version selected.</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <button
          type="button"
          className="vx-workflow-draft-mobile-button"
          onClick={handleSaveTemplate}
          disabled={!!loading.savingTemplate}
          style={{
            ...actionBtn,
            opacity: loading.savingTemplate ? 0.8 : 1,
            cursor: loading.savingTemplate ? "progress" : "pointer",
          }}
        >
          {loading.savingTemplate ? "Saving..." : "Save Template"}
        </button>

        <button
          type="button"
          className="vx-workflow-draft-mobile-button"
          onClick={handleCreateDraft}
          disabled={!selectedTemplate?.id || !!loading.creatingDraft}
          style={{
            ...generalBtn,
            opacity: !selectedTemplate?.id || loading.creatingDraft ? 0.7 : 1,
            cursor: !selectedTemplate?.id || loading.creatingDraft ? "not-allowed" : "pointer",
          }}
        >
          {loading.creatingDraft ? "Creating..." : "Create Draft"}
        </button>

        <button
          type="button"
          className="vx-workflow-draft-mobile-button"
          onClick={handlePublish}
          disabled={!publishable || !!loading.publishing}
          style={{
            ...actionBtn,
            opacity: !publishable || loading.publishing ? 0.7 : 1,
            cursor: !publishable || loading.publishing ? "not-allowed" : "pointer",
          }}
        >
          {loading.publishing ? "Publishing..." : "Publish Draft"}
        </button>
      </div>
      </div>
    </div>
  );
};

export default WorkflowDraftHeaderWidget;